// src/pages/AdminLogin.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    user_id: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://py.taih.ai/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const userData = await response.json();
        
        if (userData.is_admin) {
          // Store admin user data
          sessionStorage.setItem('adminUser', JSON.stringify(userData));
          
          // Navigate to admin dashboard
          navigate('/admin/dashboard');
        } else {
          setError('Access denied. Admin privileges required.');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', fontFamily: 'Helvetica, Arial, sans-serif' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#004494', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          }}>
            <IconButton 
              onClick={() => navigate('/')}
              sx={{ 
                color: 'white', 
                position: 'absolute',
                left: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              Admin Login
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 6, 
            maxWidth: 400, 
            mx: 'auto',
            borderRadius: 3,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AdminIcon 
              sx={{ 
                fontSize: 48, 
                color: '#004494', 
                mb: 2 
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 300, 
                mb: 1,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '32px',
                color: '#000'
              }}
            >
              Admin Access
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Please enter your admin credentials
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="User ID"
              name="user_id"
              value={formData.user_id}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&:hover fieldset': {
                    borderColor: '#004494',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#004494',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&.Mui-focused': {
                    color: '#004494',
                  }
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&:hover fieldset': {
                    borderColor: '#004494',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#004494',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  '&.Mui-focused': {
                    color: '#004494',
                  }
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                mt: 3, 
                py: 1.5,
                backgroundColor: '#004494',
                color: 'white',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(0, 68, 148, 0.3)',
                '&:hover': {
                  backgroundColor: '#003366',
                  color: 'white',
                  boxShadow: '0px 6px 16px rgba(0, 68, 148, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}